/**
 * 向量搜索服务
 * 线上：后端 OpenAI Embedding + Cloudflare Vectorize
 * 本地：先请求后端，失败则 fallback 测试 UI
 */

import knowledgeData from '../data/knowledgeBase.json';

// API 基础路径
const API_BASE = process.env.NODE_ENV === 'production' 
  ? '' 
  : 'http://localhost:8788';

// 简单词 → 高级词映射（用于本地 fallback）
const SIMPLE_TO_ADVANCED = {
  'good': ['excellent', 'outstanding', 'remarkable', 'superb', 'exceptional'],
  'bad': ['terrible', 'dreadful', 'awful', 'detrimental', 'adverse'],
  'big': ['enormous', 'massive', 'immense', 'substantial', 'considerable'],
  'small': ['tiny', 'minute', 'minuscule', 'negligible', 'trivial'],
  'important': ['crucial', 'vital', 'essential', 'significant', 'paramount'],
  'interesting': ['fascinating', 'intriguing', 'captivating', 'compelling'],
  'boring': ['tedious', 'monotonous', 'mundane', 'dull', 'uninspiring'],
  'difficult': ['challenging', 'arduous', 'demanding', 'formidable'],
  'easy': ['straightforward', 'effortless', 'uncomplicated', 'manageable'],
  'old': ['ancient', 'vintage', 'traditional', 'time-honored'],
  'new': ['novel', 'innovative', 'cutting-edge', 'contemporary'],
  'happy': ['delighted', 'thrilled', 'elated', 'overjoyed'],
  'sad': ['melancholy', 'sorrowful', 'dejected', 'disheartened'],
  'angry': ['furious', 'outraged', 'indignant', 'exasperated'],
  'clear': ['evident', 'apparent', 'obvious', 'transparent'],
  'think': ['believe', 'consider', 'reckon', 'suppose', 'contemplate'],
  'like': ['appreciate', 'enjoy', 'relish', 'favor', 'admire'],
  'want': ['desire', 'aspire', 'crave', 'yearn', 'long for'],
  'need': ['require', 'necessitate', 'demand', 'entail'],
  'get': ['obtain', 'acquire', 'attain', 'secure', 'procure'],
  'make': ['create', 'produce', 'generate', 'construct', 'establish'],
  'use': ['utilize', 'employ', 'apply', 'leverage', 'harness'],
  'show': ['demonstrate', 'illustrate', 'exhibit', 'reveal', 'manifest'],
  'help': ['assist', 'aid', 'facilitate', 'support', 'enable'],
  'change': ['transform', 'alter', 'modify', 'revolutionize', 'reshape'],
  'happen': ['occur', 'transpire', 'take place', 'come about'],
  'say': ['state', 'assert', 'declare', 'claim', 'articulate'],
  'know': ['understand', 'comprehend', 'recognize', 'realize'],
  'see': ['observe', 'perceive', 'notice', 'witness', 'discern'],
  'give': ['provide', 'offer', 'supply', 'present', 'grant'],
  'try': ['attempt', 'endeavor', 'strive', 'seek to'],
  'very': ['extremely', 'exceedingly', 'remarkably', 'exceptionally', 'immensely'],
  'really': ['genuinely', 'truly', 'absolutely', 'undoubtedly'],
  'a lot': ['considerably', 'substantially', 'significantly', 'extensively'],
  'always': ['constantly', 'perpetually', 'invariably', 'unfailingly'],
  'often': ['frequently', 'regularly', 'commonly', 'routinely'],
  'sometimes': ['occasionally', 'periodically', 'from time to time'],
  'problem': ['issue', 'challenge', 'obstacle', 'dilemma', 'predicament'],
  'result': ['outcome', 'consequence', 'effect', 'impact'],
  'thing': ['aspect', 'element', 'factor', 'component', 'phenomenon'],
  'way': ['approach', 'method', 'manner', 'technique', 'strategy'],
  'people': ['individuals', 'citizens', 'population', 'community'],
  'because': ['owing to', 'due to', 'as a result of', 'on account of'],
  'so': ['therefore', 'consequently', 'hence', 'thus', 'accordingly'],
  'but': ['however', 'nevertheless', 'nonetheless', 'yet'],
  'also': ['furthermore', 'moreover', 'additionally', 'besides'],
  'about': ['regarding', 'concerning', 'with respect to', 'in terms of'],
  'now': ['currently', 'at present', 'presently', 'nowadays'],
  'custom': ['tradition', 'convention', 'practice', 'ritual'],
  'young': ['youthful', 'juvenile', 'adolescent'],
  'economic': ['financial', 'fiscal', 'monetary', 'commercial'],
};

class VectorService {
  /**
   * 搜索知识库中语义相似的表达
   */
  async search(text, topK = 5, threshold = 0.38) {
    const isProduction = process.env.NODE_ENV === 'production';
    
    // 生产环境：只用后端 Vectorize，不用任何 fallback
    if (isProduction) {
      try {
        const response = await fetch(`/api/vector/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, topK, threshold }),
        });
        
        const data = await response.json();
        console.log('Vectorize 搜索结果:', data);
        
        if (data.success) {
          return data.results || [];
        } else {
          console.error('Vectorize 搜索失败:', data.error);
          return [];
        }
      } catch (error) {
        console.error('Vectorize API 调用失败:', error);
        return [];
      }
    }
    
    // 开发环境：尝试后端，失败则用 fallback 测试 UI
    try {
      const response = await fetch(`${API_BASE}/api/vector/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, topK, threshold }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.results && data.results.length > 0) {
          return data.results;
        }
      }
    } catch (error) {
      console.log('开发环境：后端不可用，使用 UI 测试数据');
    }
    
    return this.localFallbackSearch(text, topK);
  }
  
  /**
   * 前端 fallback 搜索（仅用于本地测试 UI）
   * 线上环境使用后端 OpenAI Embedding + Vectorize 语义搜索
   */
  localFallbackSearch(text, topK = 3) {
    const knowledgeBase = knowledgeData.knowledgeBase || [];
    
    // 简单返回知识库前几条作为测试数据，验证 UI 功能
    const testResults = knowledgeBase
      .filter(item => item.content && item.memoryLevel !== '老友')
      .slice(0, topK)
      .map(item => ({
        id: item.id,
        content: item.content,
        meaning: item.meaning,
        category: item.category,
        similarity: 0.80,
        matchedSimpleWord: '(本地测试)',
      }));
    
    console.log('[本地测试模式] 返回测试数据，线上会使用真正的语义搜索');
    return testResults;
  }
  
  /**
   * 添加新条目到向量索引
   */
  async addItem(item) {
    try {
      const response = await fetch(`${API_BASE}/api/vector/upsert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Vector upsert error:', error);
      return false;
    }
  }
  
  /**
   * 初始化向量索引
   */
  async initIndex(knowledgeBase) {
    try {
      const response = await fetch(`${API_BASE}/api/vector/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ knowledgeBase }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Vector init error:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * 检查向量服务是否可用
   */
  async isAvailable() {
    try {
      const response = await fetch(`${API_BASE}/api/vector/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: 'test', topK: 1 }),
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

const vectorService = new VectorService();
export default vectorService;
